import { useEffect, useMemo, useState, type CSSProperties, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import {
  ATTRIBUTE_DATA_TYPES,
  ATTRIBUTE_DATA_TYPE_LABEL,
  ATTRIBUTE_KEY_RE,
  attributesApi,
  type AttributeDataType,
  type AttributeRequest,
  type AttributeView,
  type EnumOption,
  unitsApi,
} from "../api/catalog";
import type { ApiError } from "../api/client";
import { detailsMap, formatApiError } from "../lib/apiError";
import { unitLabel, useUnits } from "../lib/useUnits";
import { useAuth } from "../auth/AuthContext";
import { usePrompt } from "./ConfirmDialog";
import { useToast } from "./Toast";
import { Modal } from "./Modal";
import { Select, type SelectOption } from "./Select";
import Ic from "./icons.jsx";

/** "Snaga (kW)" → "snaga_kw"; "Kućište Đ" → "kuciste_dj"; leading non-letters are dropped. */
export function slugifyAttributeKey(label: string): string {
  const latin = label
    .replace(/đ/g, "dj")
    .replace(/Đ/g, "dj")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
  return latin
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^[^a-z]+/, "")
    .replace(/_+$/, "")
    .slice(0, 50);
}

/** One row of the list editor; `rowId` is a local React key. */
type EnumRow = { rowId: number; value: string; description: string };

let enumRowSeq = 0;
function newEnumRow(value = "", description = ""): EnumRow {
  enumRowSeq += 1;
  return { rowId: enumRowSeq, value, description };
}

/** Rows → options as sent (sprint 5f §6): trimmed, rows without a value dropped, order kept. */
export function cleanEnumOptions(rows: { value: string; description: string }[]): EnumOption[] {
  const out: EnumOption[] = [];
  for (const r of rows) {
    const value = r.value.trim();
    if (!value) continue;
    out.push({ value, description: r.description.trim() || null });
  }
  return out;
}

/** The first value that appears twice, or null — the server refuses a repeated value too. */
export function duplicateEnumValue(options: EnumOption[]): string | null {
  const seen = new Set<string>();
  for (const o of options) {
    if (seen.has(o.value)) return o.value;
    seen.add(o.value);
  }
  return null;
}

export function dataTypeLabel(t: (key: string, fallback: string) => string, d: AttributeDataType): string {
  return t(`attributeDefs.dataType.${d}`, ATTRIBUTE_DATA_TYPE_LABEL[d] ?? d);
}

/**
 * Create / edit form of one codebook attribute (sprint 5f). Shared by the
 * codebook page and the part-type editor, where "Novi atribut" adds a missing
 * attribute without leaving the half-filled type. The key follows the label
 * until it is edited by hand and is fixed once the attribute exists; whether
 * the attribute is mandatory is not asked here — the part type decides. The
 * unit is picked from the unit codebook (sprint 5k), never typed; a missing one
 * is added with the "+" button (needs `units:create`) and picked at once.
 */
export function AttributeFormModal({ attribute, onClose, onSaved }: {
  /** The attribute being edited; null for a new one. */
  attribute: AttributeView | null;
  onClose: () => void;
  onSaved: (saved: AttributeView, created: boolean) => void | Promise<void>;
}) {
  const { t } = useTranslation();
  const prompt = usePrompt();
  const toast = useToast();
  const { hasPermission } = useAuth();
  const { units, refresh: refreshUnits } = useUnits();
  const canAddUnit = hasPermission("units:create");
  const editing = attribute;
  const [label, setLabel] = useState(editing?.label ?? "");
  const [key, setKey] = useState(editing?.key ?? "");
  const [keyAuto, setKeyAuto] = useState(editing === null);
  const [dataType, setDataType] = useState<AttributeDataType>(editing?.dataType ?? "TEXT");
  const [unitId, setUnitId] = useState(editing?.unitId ?? "");
  const [note, setNote] = useState(editing?.note ?? "");
  const [enumRows, setEnumRows] = useState<EnumRow[]>(() =>
    editing?.enumOptions?.length ? editing.enumOptions.map((o) => newEnumRow(o.value, o.description ?? "")) : [newEnumRow()],
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const typeOptions = useMemo<SelectOption<AttributeDataType>[]>(
    () => ATTRIBUTE_DATA_TYPES.map((d) => ({ value: d, label: dataTypeLabel(t, d) })),
    [t],
  );

  const unitOptions = useMemo<SelectOption[]>(
    () => [
      { value: "", label: t("attributes.field.unitNone", "— bez jedinice —") },
      ...units.map((u) => ({ value: u.id, label: unitLabel(u), keywords: u.name ?? undefined })),
    ],
    [units, t],
  );

  useEffect(() => {
    setError(null);
  }, [label, key, dataType, unitId, enumRows, note]);

  function updateEnumRow(i: number, change: Partial<Pick<EnumRow, "value" | "description">>) {
    setEnumRows((rows) => rows.map((r, j) => (j === i ? { ...r, ...change } : r)));
  }

  function removeEnumRow(i: number) {
    setEnumRows((rows) => rows.filter((_, j) => j !== i));
  }

  function addEnumRow() {
    setEnumRows((rows) => [...rows, newEnumRow()]);
  }

  async function addUnit() {
    const symbol = await prompt({
      title: t("units.modal.createTitle", "Nova jedinica mere"),
      message: t("units.prompt.message", "Jedinica se dodaje u šifarnik i odmah bira za ovaj atribut. Naziv i napomena se dopisuju u šifarniku jedinica."),
      placeholder: t("units.field.symbolPlaceholder", "npr. kW"),
      confirmLabel: t("action.add", "Dodaj"),
      required: true,
    });
    const trimmed = symbol?.trim();
    if (!trimmed) return;
    try {
      const created = await unitsApi.create({ symbol: trimmed });
      await refreshUnits();
      setUnitId(created.id);
      toast.success(t("units.toast.created", "Jedinica dodata u šifarnik."));
    } catch (err) {
      const e = err as ApiError;
      toast.error(e.code === "SYMBOL_TAKEN"
        ? t("units.error.symbolTaken", "Jedinica „{{symbol}}“ već postoji.", { symbol: trimmed })
        : formatApiError(e));
    }
  }

  function onLabelChange(value: string) {
    setLabel(value);
    if (keyAuto) setKey(slugifyAttributeKey(value));
  }

  function onKeyChange(value: string) {
    // Emptying the key hands it back to the label; anything typed is manual.
    setKey(value);
    setKeyAuto(value === "");
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!ATTRIBUTE_KEY_RE.test(key)) {
      setError(t("attributes.error.key", "Ključ: mala slova, cifre i donja crta, počinje slovom, 2–50 znakova (npr. snaga_kw)."));
      return;
    }
    const enumOptions = dataType === "ENUM" ? cleanEnumOptions(enumRows) : null;
    if (enumOptions && enumOptions.length === 0) {
      setError(t("attributes.error.enumEmpty", "Unesite bar jednu vrednost liste."));
      return;
    }
    const duplicate = enumOptions ? duplicateEnumValue(enumOptions) : null;
    if (duplicate) {
      setError(t("attributes.error.enumDuplicate", "Vrednost „{{value}}“ se ponavlja u listi.", { value: duplicate }));
      return;
    }
    const body: AttributeRequest = {
      key,
      label: label.trim(),
      dataType,
      unitId: unitId || null,
      enumOptions,
      note: note.trim() || null,
    };
    setSubmitting(true);
    try {
      const saved = editing ? await attributesApi.update(editing.id, body) : await attributesApi.create(body);
      await onSaved(saved, editing === null);
    } catch (err) {
      const ae = err as ApiError;
      if (ae.code === "KEY_TAKEN") {
        setError(t("attributes.error.keyTaken", "Atribut sa ključem „{{key}}“ već postoji u šifarniku.", { key }));
      } else if (ae.code === "VALIDATION" && detailsMap(ae)?.key) {
        setError(t("attributes.error.key", "Ključ: mala slova, cifre i donja crta, počinje slovom, 2–50 znakova (npr. snaga_kw)."));
      } else if (ae.code === "VALIDATION" && detailsMap(ae)?.unitId) {
        setError(t("attributes.error.unit", "Izabrana jedinica više ne postoji u šifarniku — izaberite drugu."));
      } else {
        setError(formatApiError(ae));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={editing ? t("attributes.modal.editTitle", "Izmena atributa") : t("attributes.modal.createTitle", "Novi atribut")}
      subtitle={editing ? <span style={{ fontFamily: "var(--mono)" }}>{editing.key}</span> : undefined}
      onClose={onClose}
    >
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {editing && editing.typeCount > 0 && (
          <div className="muted" style={{ fontSize: 13 }} role="note">
            {t("attributes.modal.inUseNote", "Atribut koristi {{count}} tipova — izmena važi za sve njih.", { count: editing.typeCount })}
          </div>
        )}
        <label className="field">
          <span>{t("attributes.field.label", "Naziv")} <span className="req-mark">*</span></span>
          <input
            className="input"
            value={label}
            onChange={(e) => onLabelChange(e.target.value)}
            required
            maxLength={200}
            placeholder={t("attributes.field.labelPlaceholder", "npr. Snaga")}
          />
        </label>
        {!editing && (
          <label className="field">
            <span>{t("attributes.field.key", "Ključ")} <span className="req-mark">*</span></span>
            <input
              className="input"
              value={key}
              onChange={(e) => onKeyChange(e.target.value)}
              required
              maxLength={50}
              placeholder="snaga_kw"
              style={{ fontFamily: "var(--mono)" }}
              autoCapitalize="off"
              autoCorrect="off"
              autoComplete="off"
              spellCheck={false}
              aria-invalid={key.length > 0 && !ATTRIBUTE_KEY_RE.test(key)}
            />
            <small className="muted" style={{ fontSize: 12 }}>
              {t("attributes.field.keyHint", "Popunjava se sam iz naziva. Mala slova, cifre i donja crta; ne menja se posle kreiranja.")}
            </small>
          </label>
        )}
        <div className="form-grid">
          <div className="field">
            <span>{t("attributes.field.dataType", "Tip podatka")} <span className="req-mark">*</span></span>
            <Select<AttributeDataType>
              value={dataType}
              options={typeOptions}
              onChange={setDataType}
              disabled={submitting}
              ariaLabel={t("attributes.field.dataType", "Tip podatka")}
            />
          </div>
          <div className="field">
            <span>{t("attributes.field.unit", "Jedinica")}</span>
            <div className="field-with-action">
              <Select
                value={unitId}
                options={unitOptions}
                onChange={setUnitId}
                disabled={submitting}
                placeholder={t("attributes.field.unitPick", "Izaberite jedinicu…")}
                ariaLabel={t("attributes.field.unit", "Jedinica")}
              />
              {canAddUnit && (
                <span className="tip-wrap" data-tip={t("units.new", "Nova jedinica")}>
                  <button type="button" className="btn" onClick={addUnit} disabled={submitting} aria-label={t("units.new", "Nova jedinica")}>
                    <Ic.plus />
                  </button>
                </span>
              )}
            </div>
          </div>
        </div>
        {dataType === "ENUM" && (
          <div className="field">
            <span>{t("attributes.field.enumValues", "Vrednosti liste")} <span className="req-mark">*</span></span>
            <div className="enum-rows" role="group" aria-label={t("attributes.field.enumValues", "Vrednosti liste")}>
              <div className="enum-row enum-row-head" aria-hidden="true">
                <span>{t("attributes.field.enumValue", "Vrednost")}</span>
                <span>{t("attributes.field.enumDescription", "Opis")}</span>
                <span />
              </div>
              {enumRows.map((row, i) => (
                <div key={row.rowId} className="enum-row">
                  <input
                    className="input"
                    value={row.value}
                    onChange={(e) => updateEnumRow(i, { value: e.target.value })}
                    maxLength={100}
                    placeholder={t("attributes.field.enumValuePlaceholder", "npr. B3")}
                    aria-label={t("attributes.field.enumValue", "Vrednost")}
                    autoCapitalize="off"
                  />
                  <input
                    className="input enum-row-desc"
                    value={row.description}
                    onChange={(e) => updateEnumRow(i, { description: e.target.value })}
                    maxLength={300}
                    placeholder={t("attributes.field.enumDescriptionPlaceholder", "npr. nožice, horizontalna montaža")}
                    aria-label={t("attributes.field.enumDescription", "Opis")}
                  />
                  <button type="button" className="btn sm" onClick={() => removeEnumRow(i)} aria-label={t("attributes.field.enumRemoveAria", "Ukloni vrednost: {{value}}", { value: row.value || String(i + 1) })}>
                    {t("action.remove", "Ukloni")}
                  </button>
                </div>
              ))}
            </div>
            <div>
              <button type="button" className="btn sm" onClick={addEnumRow}>
                <Ic.plus /> {t("attributes.field.enumAdd", "Dodaj vrednost")}
              </button>
            </div>
            <small className="muted" style={{ fontSize: 12 }}>
              {t("attributes.field.enumHint", "Vrednost je ono što se čuva (npr. B3); opis objašnjava šta znači i vidi se pri izboru i na kartonu.")}
            </small>
          </div>
        )}
        <label className="field">
          <span>{t("attributes.field.note", "Napomena")}</span>
          <textarea
            className="input"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={2000}
            placeholder={t("attributes.field.notePlaceholder", "npr. kako se meri, odakle se čita, kad važi…")}
            style={textarea}
          />
        </label>
        {error && <div style={errorBox} role="alert">{error}</div>}
        <div className="form-actions">
          <button type="button" className="btn" onClick={onClose}>{t("action.cancel", "Otkaži")}</button>
          <button type="submit" className="btn primary" disabled={submitting}>
            {submitting ? t("action.saving", "Čuvanje…") : t("action.save", "Sačuvaj")}
          </button>
        </div>
      </form>
    </Modal>
  );
}

const errorBox: CSSProperties = { padding: 10, background: "rgba(182,53,58,.08)", color: "var(--err)", borderRadius: 6, fontSize: 13 };
const textarea: CSSProperties = { resize: "vertical", fontFamily: "inherit", width: "100%" };
